'use client'

import React, { FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import moment from 'moment-timezone'
import TheGreatContext from '@/app/_providers/TheGreatProvider/TheGreatContext'
import {
  handleIncomingUrlData,
  initialGreatProviderState,
  theGreatProviderReducer,
} from '@/utils/providers/TheGreatProvider.utils'
import { TheGreatProviderContextT, TheGreatProviderPropsT } from '@/types/TheGreatProvider.types'
import { initialIncomingDateTime } from '@/utils/dates.utils'
import { handleIncomingQueries } from '@/utils/urls.utils'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { useOurStore } from '../StoreProvider'
import { AppState } from '@/types/store.types'
import { activeStartTimeDefault } from '@/stores/seller.slice'
import { AdmissionType } from '@/types/general.types'
import { ELEMENT_IDS } from '@/config/html-attributes.config'

/**
 * TheGreatProvider - working it's way up to top provider
 * @param children
 * @param isEvents
 * @constructor
 */
const TheGreatProvider: FunctionComponent<PropsWithChildren<TheGreatProviderPropsT>> = ({
  children,
  isEvents,
}) => {
  const searchParams = useSearchParams()
  const { timezone } = useSeller()
  const urlParamData = useMemo(() => handleIncomingQueries(searchParams), [searchParams])
  const cartId = useOurStore((state) => state.cartId)
  // eslint-disable-next-line no-underscore-dangle
  const hasHydrated = useOurStore((state) => state._hasHydrated)
  const updatePartial = useOurStore((state) => state.updatePartial)
  const admissionType = useOurStore((state) => state.admissionType)
  const resetAdmissionType = useOurStore((state) => state.resetAdmissionType)
  const [{ incomingQueries }, setState] = useReducer(
    theGreatProviderReducer,
    initialGreatProviderState,
  )
  const prevAppliedIncomingDateTime = useRef<string | null>(null)
  const handledScroll = useRef(false)

  // Hooks
  /**
   * Sync incomingQueries from URL params.
   * Runs on mount AND when searchParams change (e.g. hydration populates params
   * that were null during SSR, or navigation changes the URL).
   */
  const prevUrlParamDataString = useRef<string | null>(null)

  useEffect(() => {
    const urlParamDataString = JSON.stringify(urlParamData)
    if (prevUrlParamDataString.current === urlParamDataString) return
    prevUrlParamDataString.current = urlParamDataString

    const nextIncomingQueries = handleIncomingUrlData(urlParamData, timezone)
    setState((state) => {
      const nextStateQueries = {
        ...initialGreatProviderState.incomingQueries,
        ...nextIncomingQueries,
      }

      // Preserve existing dateTime when URL params are stripped (e.g. by step navigation)
      if (!nextStateQueries?.dateTime?.date && state?.incomingQueries?.dateTime?.date) {
        nextStateQueries.dateTime = state.incomingQueries.dateTime
      }

      // Preserve existing promo codes when URL params are stripped by navigation.
      if (
        (!nextStateQueries?.promoCodes || nextStateQueries.promoCodes.length === 0)
        && state?.incomingQueries?.promoCodes?.length
      ) {
        nextStateQueries.promoCodes = state.incomingQueries.promoCodes
      }

      // Preserve skip when the initial deep-link param is removed by client-side navigation.
      if (!urlParamData.skip.length && state?.incomingQueries?.skip) {
        nextStateQueries.skip = state.incomingQueries.skip
      }

      if (JSON.stringify(state.incomingQueries) === JSON.stringify(nextStateQueries)) {
        return state
      }
      return { incomingQueries: nextStateQueries }
    })
  }, [urlParamData, timezone])

  // Apply incoming dateTime to store
  useEffect(() => {
    // Persisted state lands after this effect's first pass, so `cartId` and `admissionType` both
    // read as their defaults until the store has hydrated. Acting on that would apply the deep link
    // over a cart, and miss a day pass flow the user is already in.
    if (!hasHydrated) return
    // if the user has a cart, don't apply incoming dateTime
    if (cartId) return

    const incomingDateTime = handleIncomingUrlData(urlParamData, timezone).dateTime

    const nextStoreState: Partial<AppState> = {}
    const appliedKey = JSON.stringify({ dateTime: incomingDateTime, timezone })

    // Prevent applying the incoming dateTime if it hasn't changed since the last application
    if (prevAppliedIncomingDateTime.current === appliedKey) return

    if (incomingDateTime?.date) {
      nextStoreState.activeDate = moment.tz(`${incomingDateTime.date} 10:00`, timezone).utc()
        .format()
    }

    if (incomingDateTime?.withTime) {
      const formattedTime = [
        incomingDateTime.time.hours.toString().padStart(2, '0'),
        incomingDateTime.time.minutes.toString().padStart(2, '0'),
      ].join(':')
      const incomingStartTime = moment.tz(
        `${incomingDateTime.date} ${formattedTime}`,
        timezone,
      )
        .utc()
        .format()

      nextStoreState.activeStartTime = {
        ...activeStartTimeDefault,
        startTime: incomingStartTime,
      }
      // let the app know that this startTime is coming from the url param
      nextStoreState.startTimeFromUrlParam = true

      // A time only means something in timed ticketing, so it wins over a day pass the store is
      // holding from an earlier visit — the same rule `handleIncomingUrlData` applies to
      // `admission_type=daypass&time=`, see docs/admission-type-switching-bug.md §4. It has to
      // happen here rather than in `Timeslots`: switching there runs after the day pass
      // auto-select has already overwritten this start time and advanced the step.
      if (admissionType === AdmissionType.DayPass) {
        resetAdmissionType({ admissionType: AdmissionType.TimedTicket })
      }
    }

    if (!Object.keys(nextStoreState).length) return

    prevAppliedIncomingDateTime.current = appliedKey
    updatePartial(nextStoreState)
  }, [cartId, hasHydrated, timezone, updatePartial, urlParamData, admissionType, resetAdmissionType])

  const scrollToMainContent = useCallback((): void => {
    if (handledScroll.current) return

    const { skip, dateTime, promoCodes } = incomingQueries
    const hasIncomingParams = skip
      || (dateTime && dateTime.date.length > 0)
      || (promoCodes && promoCodes.length > 0)

    if (!hasIncomingParams) return

    const scrollTarget = document.getElementById(ELEMENT_IDS.MAIN_CONTENT)

    if (scrollTarget) {
      scrollTarget.scrollIntoView(true)
      updatePartial({ doActionContainerScroll: false })
    }

    handledScroll.current = true
  }, [incomingQueries, updatePartial])

  const handleSetIncomingPromoCodes = useCallback((promoCodes: string[]): void => {
    setState((state) => ({
      incomingQueries: {
        ...state.incomingQueries,
        promoCodes,
      },
    }))
  }, [])

  /**
   * Reset incoming date time
   * meant to be used by child components
   * @returns void
   */
  const handleResetIncomingDateTime = useCallback((): void => {
    setState((state) => ({
      incomingQueries: {
        ...state.incomingQueries,
        dateTime: {
          ...initialIncomingDateTime,
          time: { ...initialIncomingDateTime.time },
        },
      },
    }))
  }, [])

  const value = useMemo((): TheGreatProviderContextT => ({
    handleSetIncomingPromoCodes,
    handleResetIncomingDateTime,
    scrollToMainContent,
    incomingQueries,
    isEvents,
  }), [
    handleResetIncomingDateTime,
    handleSetIncomingPromoCodes,
    scrollToMainContent,
    incomingQueries,
    isEvents,
  ])
  return (
    <TheGreatContext.Provider value={value}>
      {children}
    </TheGreatContext.Provider>
  )
}

export default TheGreatProvider
